import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { SkeletonText } from '../../../Components/Common/Skeleton';
import './procurementDetail.css';

const RING_RADIUS = 40;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ done, total }) => {
  const percent = total ? Math.round((done / total) * 100) : 0;
  return (
    <svg
      className='pd-ring'
      viewBox='0 0 100 100'
      role='img'
      aria-label={`${done} of ${total} steps completed`}
    >
      <circle className='pd-ring__track' cx='50' cy='50' r={RING_RADIUS} />
      <circle
        className='pd-ring__bar'
        cx='50'
        cy='50'
        r={RING_RADIUS}
        strokeDasharray={RING_LENGTH.toFixed(1)}
        strokeDashoffset={(RING_LENGTH * (1 - percent / 100)).toFixed(1)}
        transform='rotate(-90 50 50)'
      />
      <text className='pd-ring__value' x='50' y='53' textAnchor='middle'>
        {percent}%
      </text>
      <text className='pd-ring__caption' x='50' y='67' textAnchor='middle'>
        {done} of {total} steps
      </text>
    </svg>
  );
};

ProgressRing.propTypes = {
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

/**
 * Header of the procurement detail page: status, identity, the facts people
 * look for first, overall progress and the one next action.
 */
const ProcurementHero = ({
  loading,
  title,
  stage,
  badges,
  meta,
  facts,
  progress,
  cta,
  onBack,
}) => (
  <header className='pd-hero'>
    <div>
      <div className='pd-hero__top'>
        {onBack && (
          <button
            type='button'
            className='pd-hero__back'
            onClick={onBack}
            aria-label='Back to procurement list'
            title='Back to procurement list'
          >
            <i className='ri-arrow-left-line' aria-hidden='true' />
          </button>
        )}
        {stage && (
          <span className={`pd-pill pd-pill--${stage.key}`}>{stage.label}</span>
        )}
        {badges}
      </div>

      {loading ? (
        <div className='py-2'>
          <SkeletonText lines={2} />
        </div>
      ) : (
        <>
          <h1 className='pd-hero__title'>{title}</h1>
          {meta && <div className='pd-hero__meta'>{meta}</div>}
          {facts?.length > 0 && (
            <dl className='pd-facts'>
              {facts.map(fact => (
                <div className='pd-fact' key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd className={fact.warn ? 'is-warn' : undefined}>
                    {fact.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </>
      )}
    </div>

    <div className='pd-hero__side'>
      {progress && <ProgressRing done={progress.done} total={progress.total} />}
      {cta && (
        <Button className='pd-hero__cta' onClick={cta.onClick}>
          {cta.label}
          <i
            className='ri-arrow-right-line align-middle ms-2'
            aria-hidden='true'
          />
        </Button>
      )}
    </div>
  </header>
);

ProcurementHero.propTypes = {
  loading: PropTypes.bool,
  title: PropTypes.node,
  stage: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  badges: PropTypes.node,
  meta: PropTypes.node,
  facts: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      warn: PropTypes.bool,
    })
  ),
  progress: PropTypes.shape({
    done: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }),
  cta: PropTypes.shape({
    label: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  onBack: PropTypes.func,
};

export default ProcurementHero;
